import { useEffect, useState } from "react";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours client side too

const _memo = {};

async function fetchReviews(storeSlug) {
    const now = Date.now();
    if (_memo[storeSlug] && _memo[storeSlug].expires > now) {
        return _memo[storeSlug].data;
    }
    const res = await fetch(`${API}/reviews/${storeSlug}`);
    if (!res.ok) throw new Error("reviews fetch failed");
    const data = await res.json();
    _memo[storeSlug] = { expires: now + CACHE_TTL, data };
    return data;
}

/**
 * Live Google Reviews badge — small inline strip that swaps the static
 * "4.9★ from 4,300+ reviews" copy for the LIVE Place Details rating + count.
 * Falls back gracefully to the static text when Google is unreachable.
 *
 * Props:
 *   storeSlug — "queens-long-island" or "staten-island"
 *   tone      — "light" (cream text on dark bg) or "dark" (zen-ink on cream)
 *   showLink  — when true, makes the strip a clickable Google Maps link
 */
export default function LiveReviewBadge({
    storeSlug = "queens-long-island",
    tone = "light",
    showLink = true,
    testid = "live-reviews-badge",
}) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchReviews(storeSlug)
            .then((d) => {
                if (!cancelled) {
                    Promise.resolve().then(() => setData(d));
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    const rating = data?.rating ?? 4.9;
    const count = data?.user_rating_count ?? 3259;
    const url = data?.google_maps_uri;
    const ratingFmt = Number(rating).toFixed(1);
    const countFmt = `${Number(count).toLocaleString()}+`;

    const text = (
        <>
            <Star
                size={12}
                fill="currentColor"
                className={tone === "light" ? "text-zen-olive-light" : "text-zen-olive"}
            />
            <span data-testid={`${testid}-rating`}>{ratingFmt}★</span>
            <span className="opacity-70">·</span>
            <span data-testid={`${testid}-count`}>
                {countFmt} Google reviews
            </span>
        </>
    );

    const className =
        "inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium " +
        (tone === "light" ? "text-zen-olive-light" : "text-zen-olive");

    if (showLink && url) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testid}
                className={`${className} hover:opacity-80`}
            >
                {text}
            </a>
        );
    }
    return (
        <span data-testid={testid} className={className}>
            {text}
        </span>
    );
}

/**
 * Full Google Reviews carousel — fetches and renders up to 5 reviews with
 * author photo, rating, relative time, and text. Used on store detail pages.
 */
export function GoogleReviewsCarousel({ storeSlug, testid = "google-reviews-carousel" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchReviews(storeSlug)
            .then((d) => {
                if (!cancelled) {
                    Promise.resolve().then(() => setData(d));
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    if (!data?.reviews?.length) return null;

    return (
        <section data-testid={testid} className="bg-zen-bg border-t border-zen-ink/10">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-3">
                            Live Google reviews
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl font-light text-zen-ink leading-tight">
                            {Number(data.rating || 4.9).toFixed(1)}
                            <span className="text-zen-olive">★</span>{" "}
                            <span className="text-zen-ink-soft text-base sm:text-lg">
                                from {Number(data.user_rating_count || 0).toLocaleString()} reviews
                            </span>
                        </h2>
                    </div>
                    {data.google_maps_uri && (
                        <a
                            href={data.google_maps_uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1 hover:border-zen-olive"
                        >
                            See all on Google Maps
                        </a>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.reviews.slice(0, 6).map((r, i) => (
                        <article
                            key={`${r.author_name || "anon"}-${r.publish_time || i}`}
                            data-testid={`${testid}-item-${i}`}
                            className="bg-zen-surface border border-zen-ink/10 p-5 flex flex-col gap-3"
                        >
                            <div className="flex items-center gap-3">
                                {r.author_photo ? (
                                    <img
                                        src={r.author_photo}
                                        alt={r.author_name || ""}
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        className="w-10 h-10 rounded-full object-cover bg-zen-bg"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-zen-olive/20 inline-flex items-center justify-center text-zen-olive text-sm font-medium">
                                        {(r.author_name || "?").charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-zen-ink truncate">
                                        {r.author_name || "Google user"}
                                    </p>
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                                        {r.relative_time || ""}
                                    </p>
                                </div>
                                <div className="text-zen-olive text-sm font-semibold tabular-nums">
                                    {Number(r.rating || 0).toFixed(0)}★
                                </div>
                            </div>
                            <p className="text-sm text-zen-ink-soft leading-relaxed line-clamp-6">
                                {r.text}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
